import { supabase } from '../lib/supabase'

/**
 * Servicio del historial de cambios de un ticket (audit trail).
 * Cada registro guarda un campo modificado: valor anterior, valor nuevo, autor
 * y fecha. Tabla: `Tickets.ticketHistory`.
 *
 * @namespace ticketHistoryService
 */
const SCHEMA = 'Tickets'
const TABLE = 'ticketHistory'

export const ticketHistoryService = {
  /**
   * Registra uno o varios cambios. Ignora entradas sin cambio real.
   * @param {Array<{ticketId:(number|string), fieldName:string, oldValue:*, newValue:*, changedBy?:string}>} entries
   * @returns {Promise<{success:boolean, error?:string}>}
   */
  async logMany(entries) {
    try {
      const rows = (entries || [])
        .filter(
          (e) => e && String(e.oldValue ?? '') !== String(e.newValue ?? '')
        )
        .map((e) => ({
          ticketId: e.ticketId,
          fieldName: e.fieldName,
          oldValue: e.oldValue == null ? null : String(e.oldValue),
          newValue: e.newValue == null ? null : String(e.newValue),
          changedBy: e.changedBy ?? null,
          createdAt: new Date().toISOString(),
        }))

      if (rows.length === 0) return { success: true }

      const { error } = await supabase.schema(SCHEMA).from(TABLE).insert(rows)
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error registrando historial:', error)
      return { success: false, error: error.message }
    }
  },

  /**
   * Registra un único cambio.
   * @param {{ticketId:(number|string), fieldName:string, oldValue:*, newValue:*, changedBy?:string}} entry
   * @returns {Promise<{success:boolean, error?:string}>}
   */
  async log(entry) {
    return this.logMany([entry])
  },

  /**
   * Obtiene el historial de un ticket, más reciente primero.
   * @param {number|string} ticketId
   * @returns {Promise<{success:boolean, data:Array|null, error?:string}>}
   */
  async getByTicket(ticketId) {
    try {
      const { data, error } = await supabase
        .schema(SCHEMA)
        .from(TABLE)
        .select('*')
        .eq('ticketId', ticketId)
        .order('createdAt', { ascending: false })
      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Error obteniendo historial:', error)
      return { success: false, data: null, error: error.message }
    }
  },
}

export default ticketHistoryService
